import type { TbHttp } from "./http";
import type { Aggregation, AttributeEntry, AttributeScope, EntityRef, TbTsPoint } from "./types";

export function getAttributes(
  http: TbHttp,
  entity: EntityRef,
  scope: AttributeScope = "SERVER_SCOPE",
  keys?: readonly string[],
): Promise<AttributeEntry[]> {
  const p = new URLSearchParams({ scope });
  if (keys?.length) p.set("keys", keys.join(","));
  return http.get(`/api/plugins/telemetry/${entity.type}/${entity.id}/values/attributes?${p}`);
}

export function getTimeseries(
  http: TbHttp,
  entity: EntityRef,
  opts: { keys: readonly string[]; startTs: number; endTs: number; limit?: number; agg?: Aggregation; interval?: number },
): Promise<Record<string, TbTsPoint[]>> {
  const p = new URLSearchParams({
    keys: opts.keys.join(","),
    startTs: String(opts.startTs),
    endTs: String(opts.endTs),
    agg: opts.agg ?? "NONE",
  });
  if (opts.limit) p.set("limit", String(opts.limit));
  if (opts.interval) p.set("interval", String(opts.interval));
  return http.get(`/api/plugins/telemetry/${entity.type}/${entity.id}/values/timeseries?${p}`);
}

export function getLatestTimeseries(
  http: TbHttp,
  entity: EntityRef,
  keys: readonly string[],
): Promise<Record<string, TbTsPoint[]>> {
  const p = new URLSearchParams({ keys: keys.join(",") });
  return http.get(`/api/plugins/telemetry/${entity.type}/${entity.id}/values/timeseries?${p}`);
}

export function saveSharedAttribute(
  http: TbHttp,
  entity: EntityRef,
  attrs: Record<string, unknown>,
): Promise<void> {
  return http.post<void>(`/api/plugins/telemetry/${entity.type}/${entity.id}/SHARED_SCOPE`, attrs);
}

/**
 * Write a SERVER_SCOPE attribute on a TB entity. Same wire shape as
 * `saveSharedAttribute`; only the scope segment of the URL differs.
 * SERVER_SCOPE is where operator-set configuration lives (e.g. the
 * per-site discharge price on the Revenue page) — tenant/customer
 * readable but never pushed to devices.
 */
export function saveServerAttribute(
  http: TbHttp,
  entity: EntityRef,
  attrs: Record<string, unknown>,
): Promise<void> {
  return http.post<void>(`/api/plugins/telemetry/${entity.type}/${entity.id}/SERVER_SCOPE`, attrs);
}

/**
 * Discover every timeseries key the EMS server has ever recorded for an
 * entity. Returns the full list as written by upstream services — both
 * the raw cloud-connector device keys AND any AE2-derived buckets
 * (EP_d, EP_h, P_EP_5, etc.) that scheduler jobs publish on the same
 * device. The Historical analyzer's "Browse all telemetry" panel uses
 * this so the operator sees what's actually available, not a hardcoded
 * subset.
 *
 * On 404 / network failure returns `[]` so callers can render a graceful
 * empty state without throwing.
 */
export function getDeviceTimeseriesKeys(
  http: TbHttp,
  entity: EntityRef,
): Promise<string[]> {
  return http
    .get<string[] | null>(`/api/plugins/telemetry/${entity.type}/${entity.id}/keys/timeseries`)
    .then((r) => Array.isArray(r) ? r : [])
    .catch(() => []);
}
